import React from 'react';
import styled from 'styled-components';
import Section from './ui/Section';
import Container from './ui/Container';
import Button from './ui/Button';
import { ArrowRight } from './ui/Icons';
import BookConsultationButton from './BookConsultationButton';
import { CONTACT_EMAIL } from '../data/site';

const Panel = styled.div`
  position: relative;
  overflow: hidden;
  padding: clamp(2.25rem, 6vw, 4.5rem);
  border-radius: var(--radius-lg);
  background: var(--bg-2);
  border: 1px solid var(--border-strong);
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) auto;
  gap: 2rem 3rem;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--accent);
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Heading = styled.h3`
  font-size: var(--fs-h2);
  line-height: 1.12;
  letter-spacing: -0.02em;
`;

const Sub = styled.p`
  margin-top: 1rem;
  color: var(--text-2);
  max-width: 54ch;
  font-size: 1.0625rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  min-width: 260px;

  @media (max-width: 860px) {
    min-width: 0;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmailLine = styled.p`
  margin-top: 1.25rem;
  font-size: var(--fs-small);
  color: var(--text-3);

  a {
    color: var(--text-2);
    text-decoration: underline;
    text-underline-offset: 3px;
    word-break: break-word;
  }

  a:hover {
    color: var(--text-1);
  }
`;

const COPY = {
  en: {
    title: 'Tell us what you need to ship.',
    body: "A short call to understand your goals and constraints. You'll leave with clear next steps whether or not you work with us.",
    email: 'Prefer email?',
    book: 'Book a consultation',
    send: 'Send a message',
  },
  es: {
    title: 'Cuéntanos qué necesitas publicar.',
    body: 'Una llamada corta para entender tus metas y tus límites. Sales con los próximos pasos claros, trabajes con nosotros o no.',
    email: '¿Prefieres correo?',
    book: 'Reservar una consulta',
    send: 'Enviar un mensaje',
  },
};

interface CallToActionProps {
  title?: string;
  lang?: 'en' | 'es';
}

const CallToAction: React.FC<CallToActionProps> = ({ title, lang = 'en' }) => {
  const copy = COPY[lang === 'es' ? 'es' : 'en'];
  return (
    <Section aria-labelledby="cta-title">
      <Container>
        <Panel>
          <div>
            <Heading id="cta-title">{title ?? copy.title}</Heading>
            <Sub>{copy.body}</Sub>
            <EmailLine>
              {copy.email} <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </EmailLine>
          </div>
          <Actions>
            <BookConsultationButton size="lg" label={copy.book} />
            <Button to="/contact" variant="secondary" size="lg">
              {copy.send} <ArrowRight />
            </Button>
          </Actions>
        </Panel>
      </Container>
    </Section>
  );
};

export default CallToAction;
